/**
 * Meta Pixel (browser) payloads aligned with common `contents` / line-item patterns.
 * @see https://developers.facebook.com/docs/meta-pixel/implementation/conversion-tracking/
 * @see https://www.facebook.com/business/help/402791146561655?id=1205376682832142
 */
import {
    ApiShopperLandingPageLink,
    centsToCurrencyUnits,
} from '@storefronts/common-types'
import { fbEvent } from '../../meta'
import { FBEvent, MetaPixelContentItem } from '../../meta/types'
import { getTrackingInfo } from '../components/landing-page/hardcoded/adTrackingContent'
import { Cart, VariantWithImage } from '../types'
import { getDiscountedCartTotal } from '../utils/cartTools'
import { transformLinkToVariant } from '../utils/helpers'

const CURRENCY = 'USD'

export class MetaPixelTracker {
    private static contentsForVariant(
        product: VariantWithImage,
        quantity: number,
    ): MetaPixelContentItem[] {
        const info = getTrackingInfo(product)
        const unitPrice = centsToCurrencyUnits(product.finalPrice)
        if (info) {
            return [
                {
                    id: info.contentIds[0] ?? product.link.shopifyId,
                    quantity,
                    item_price: unitPrice,
                    title: info.productName,
                },
            ]
        }
        return [
            {
                id: product.link.shopifyId,
                quantity,
                item_price: unitPrice,
                title: product.link.title,
            },
        ]
    }

    private static cartLineItemsSummary(cart: Cart): {
        contents: MetaPixelContentItem[]
        content_ids: string[]
        num_items: number
        value: number
    } {
        const contents: MetaPixelContentItem[] = cart.items.map(
            ({ variant, quantity }) => ({
                id: variant.link.shopifyId,
                quantity,
                item_price: centsToCurrencyUnits(variant.finalPrice),
                title: variant.link.title,
            }),
        )
        const content_ids = contents.map((c) => c.id).filter(Boolean)
        const num_items = cart.items.reduce((sum, i) => sum + i.quantity, 0)
        const value = centsToCurrencyUnits(
            getDiscountedCartTotal(cart, false, 0),
        )
        return { contents, content_ids, num_items, value }
    }

    private static buildEventPayload(
        clientHash: string,
        eventName: string,
        product?: VariantWithImage,
        options?: {
            value?: number
            num_items?: number
            content_name?: string
            contents?: MetaPixelContentItem[]
        },
    ): FBEvent {
        const basePayload: FBEvent = {
            eventName,
            clientHash,
        }

        if (!product) {
            return {
                ...basePayload,
                ...options,
            }
        }

        const info = getTrackingInfo(product)
        const lineQty = options?.num_items ?? 1
        const value =
            options?.value ??
            centsToCurrencyUnits(Number(product.finalPrice) * lineQty)

        const productPayload: FBEvent = {
            ...basePayload,
            value,
            num_items: lineQty,
            currency: CURRENCY,
        }

        const contents =
            options?.contents ??
            MetaPixelTracker.contentsForVariant(product, lineQty)

        if (info) {
            return {
                ...productPayload,
                content_type: 'product_group',
                content_name: info.productName,
                content_ids: info.contentIds,
                content_category: info.categoryName,
                contents,
            }
        }

        return {
            ...productPayload,
            content_type: 'product',
            content_name: product.link.title,
            content_ids: [product.link.shopifyId],
            contents,
        }
    }

    /** ViewContent from a landing page link (PDP, product scroll, etc.). */
    static trackViewContentFromLink(
        clientHash: string,
        link: ApiShopperLandingPageLink,
    ): void {
        fbEvent(
            MetaPixelTracker.buildEventPayload(
                clientHash,
                'ViewContent',
                transformLinkToVariant(link),
                { num_items: 1 },
            ),
        )
    }

    static trackViewContentCartPage(clientHash: string, cart: Cart): void {
        const { contents, content_ids, num_items, value } =
            MetaPixelTracker.cartLineItemsSummary(cart)
        fbEvent({
            eventName: 'ViewContent',
            clientHash,
            content_name: 'Cart Page',
            content_type: 'product_group',
            currency: CURRENCY,
            content_ids,
            contents,
            num_items,
            value,
        })
    }

    static trackViewContentProductListing(clientHash: string): void {
        fbEvent(
            MetaPixelTracker.buildEventPayload(
                clientHash,
                'ViewContent',
                undefined,
                {
                    content_name: 'Product Listing Page',
                },
            ),
        )
    }

    static trackAddToCart(
        clientHash: string,
        product: VariantWithImage,
        quantity: number,
    ): void {
        fbEvent(
            MetaPixelTracker.buildEventPayload(
                clientHash,
                'AddToCart',
                product,
                {
                    num_items: quantity,
                },
            ),
        )
    }

    static trackInitiateCheckout(
        clientHash: string,
        options?: { cart?: Cart; buyNowVariant?: VariantWithImage },
    ): void {
        if (options?.cart && options.cart.items.length > 0) {
            const { contents, content_ids, num_items, value } =
                MetaPixelTracker.cartLineItemsSummary(options.cart)
            fbEvent({
                eventName: 'InitiateCheckout',
                clientHash,
                content_type: 'product_group',
                currency: CURRENCY,
                content_ids,
                contents,
                num_items,
                value,
            })
            return
        }
        if (options?.buyNowVariant) {
            fbEvent(
                MetaPixelTracker.buildEventPayload(
                    clientHash,
                    'InitiateCheckout',
                    options.buyNowVariant,
                    { num_items: 1 },
                ),
            )
            return
        }
        fbEvent({
            eventName: 'InitiateCheckout',
            clientHash,
            currency: CURRENCY,
            value: 0,
            num_items: 0,
        })
    }

    /**
     * ViewContent for catalog SKUs with hardcoded ad-tracking metadata only.
     * No-op when the product is not in the mapping.
     */
    static trackProductView(
        clientHash: string,
        link: ApiShopperLandingPageLink,
    ): void {
        const variant = transformLinkToVariant(link)
        if (!getTrackingInfo(variant)) return
        fbEvent(
            MetaPixelTracker.buildEventPayload(
                clientHash,
                'ViewContent',
                variant,
                {
                    num_items: 1,
                },
            ),
        )
    }
}
